<template>
    <div
        id="timeline-list-root"
        class="root"
        tabindex="0"
        :aria-label="`List of timelines in ${project ? project.name : 'the current project'}`"
        autofocus
    >
        <div>
            <b-container fluid>
                <b-row>
                    <b-col style="margin-top: -5px; margin-bottom: -5px">
                        <b-input-group>
                            <b-input-group-prepend style="width: 40px;">
                                <b-input-group-text
                                    variant="light"
                                    disabled
                                >
                                    <i class="fas fa-search" />
                                </b-input-group-text>
                            </b-input-group-prepend>
                            <b-input
                                id="timeline-search"
                                v-model="timelineFilter"
                                name="timeline-search"
                                placeholder="Search timelines"
                                type="search"
                                autocomplete="off"
                            />
                        </b-input-group>
                    </b-col>
                </b-row>
            </b-container>
        </div>
        <div
            class="timeline-scroll-root"
            style="margin-top: 18px;"
        >
            <b-container
                fluid
                style="padding: 0"
            >
                <div v-if="timelines == null">
                    <b-spinner style="display: block;margin: auto;margin-top: 25vh;" />
                </div>
                <div v-else>
                    <template v-if="categories.length == 0 || (categories.length == 1 && categories[0].folder.id == null)">
                        <div
                            class="d-flex align-content-between flex-wrap"
                            style="width: 100%; padding-bottom: 20px;"
                        >
                            <div
                                v-for="timeline in timelinesSorted"
                                :key="timeline.id"
                            >
                                <timelines-element :timelineid="timeline.id" />
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <!--<div v-for="entry in categories" :key="`_entry_${entry.folder.id}_${(entry.timelines.length == 0 ? '_e' : '_v')}`" style="width: 100%;">-->
                        <div
                            v-for="entry in categories"
                            :key="`_entry_${entry.folder.id}}`"
                            style="width: 100%;"
                        >
                            <div style="background-color: #e8e6e5; box-shadow: 0px 2px 3px rgba(0,0,0,0.2);">
                                <div style="display: flex; flex-direction: row;">
                                    <b-btn
                                        v-b-toggle="`__tl_list_cat_collapse_${entry.folder.id}`"
                                        :disabled="entry.timelines.length == 0"
                                        block
                                        variant="light"
                                    >
                                        <span class="open-folder"><i class="fas fa-folder-open" /></span><span class="closed-folder"><i :class="entry.timelines.length == 0 ? 'fas fa-folder' : 'fas fa-folder-plus'" /></span> {{ entry.folder.name }} <template v-if="entry.timelines.length == 0">
                                            (empty)
                                        </template>
                                    </b-btn>
                                    <folder-gear-dropdown :folder="entry.folder" />
                                </div>
                                <b-collapse
                                    :id="`__tl_list_cat_collapse_${entry.folder.id}`"
                                    :visible="entry.timelines.length > 0 && entry.open"
                                    @hide="minimizeFolder(categories, entry.folder.id, true)"
                                    @show="minimizeFolder(categories, entry.folder.id, false)"
                                >
                                    <div
                                        class="d-flex align-content-between flex-wrap"
                                        style="width: 100%; padding-bottom: 20px;"
                                    >
                                        <div
                                            v-for="timeline in entry.timelines"
                                            :key="timeline.id"
                                        >
                                            <timelines-element :timelineid="timeline.id" />
                                        </div>
                                    </div>
                                </b-collapse>
                            </div>
                        </div>
                    </template>
                </div>
            </b-container>
        </div>
    </div>
</template>

<script>
import Vue from 'vue';
import Timeline from '@/models/Timeline.ts';
import UserGroup from '@/models/UserGroup.ts';
import User from '@/models/User.ts';
import TimelinesElement from './TimelinesElement.vue';
import { mapGetters } from 'vuex';
import FolderCommands from '../../networking/Folder';
import FolderGearDropdown from './Folders/FolderGearDropdown';
import project from '@/store/modules/project';

export default Vue.extend(
    {
        components: 
        {
            TimelinesElement,
            FolderGearDropdown
        },
        data()
        {
            return {
                timelineFilter: "",
                minimizedFolders: null
            };
        },
        computed: 
        {
            ...mapGetters([
                'timelines',
                'folders',
                'checkFolderEdit'
            ]),
            project()
            {
                return this.$store.state.project.project;
            },
            localStorageKey () 
            {
                const project = this.$store.state.project.project || { id: '?' };
                return `timeline-list-minimized-folders-${project.id}`;
            },
            timelinesArray()
            {
                return Array.from(this.timelines.values());
            },
            canSeeEmptyCategories()
            {
                // TODO: Hide categories based on permissions
                return this.checkFolderEdit();
            },
            currentMinimizedFolders() 
            {
                const minimizedFolders = {};
                if (this.minimizedFolders === null)
                {
                    try
                    {
                        const value = localStorage.getItem(this.localStorageKey);
                        if (value)
                        {
                            const parsed = JSON.parse(value);
                            if (parsed)
                            {
                                Object.assign(minimizedFolders, parsed);
                            }
                        }
                    }
                    catch (e)
                    {
                        console.error(e);
                    }
                    
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.minimizedFolders = minimizedFolders;
                }
                else
                {
                    Object.assign(minimizedFolders, this.minimizedFolders);
                }
                return minimizedFolders;
            },
            categories()
            {
                let res = [];

                const minimizedFolders = this.currentMinimizedFolders;
                
                /** @type Timeline[] */
                const sorted = Array.from(this.timelinesSorted);
                for(let folder of this.sortedFolderList)
                {
                    let entry = { folder, timelines: [], open: !minimizedFolders[folder.id] };
                    for(let i = 0; i < sorted.length; ++i)
                    {
                        let tl = sorted[i];
                        if (tl.folderid == folder.id)
                        {
                            entry.timelines.push(tl);
                            sorted.splice(i, 1);
                            --i;
                        }
                    }
                    res.push(entry);
                }
                res.push({ folder: {  id: null, name: "Uncategorized" }, timelines: sorted, open: !minimizedFolders[-1] });

                if (!this.canSeeEmptyCategories)
                {
                    res = res.filter(a=>a.timelines.length > 0);
                }
                return res;
            },
            unsortedFolderList()
            {
                return Array.from(this.folders.values());
            },
            sortedFolderList()
            {
                let result = [];
                if (this.folders.size > 0)
                {
                    let unsorted = this.unsortedFolderList;
                    result = unsorted.sort((a, b) => 
                    {
                        // numeric == natural sort, base sensitivity == case insensitive
                        return a.name.trim().localeCompare(b.name.trim(), undefined, {numeric: true, sensitivity: 'base'});
                    });
                }
                return result;
            },
            timelinesSorted()
            {
                let result = [];
                if (this.timelines.size > 0)
                {
                    let unsorted = this.timelinesArray;
                    let trimmed = this.timelineFilter.trim().toLowerCase();
                    if (trimmed.length > 0)
                    {
                        unsorted = unsorted.filter((el)=>el.name.toLowerCase().includes(trimmed));
                    }
                    result = unsorted.sort((a, b) => 
                    {
                        // numeric == natural sort, base sensitivity == case insensitive
                        return a.name.trim().localeCompare(b.name.trim(), undefined, {numeric: true, sensitivity: 'base'});
                    });
                }
                return result;
            }
        },
        mounted()
        {
            setTimeout(() => 
            {
                if(this != null && this.timelines != null && this.timelines.size == 0)
                {
                    this.$root.$emit('bv::show::tooltip', 'create-timeline-tooltip');
                }
            }, 4000);
            const element = document.getElementById("timeline-list-root");
            if (element)
            {
                element.focus();
            }
        },
        methods: 
	    {
	        async setTimelineFolder(timeline, folderid)
	        {
	            await FolderCommands.SetTimelineFolderByID(timeline.id, folderid);
	        },
	        minimizeFolder(categories, folderid, minimize)
	        {
	            if (folderid === null || folderid === undefined)
	            {
	                folderid = -1;
	            }
                
	            const minimizedFolders = this.currentMinimizedFolders;
	            if (minimize)
	            {
	                minimizedFolders[folderid] = true;
	            }
	            else
	            {
	                delete minimizedFolders[folderid];
	            }
	            localStorage.setItem(this.localStorageKey, JSON.stringify(minimizedFolders));
	        }
	    }
    });
</script>


<style lang="scss" scoped>
.timeline-scroll-root
{
	margin-right: -8px;
	background-color: #efeceb;
	height: calc(93vh - 175px);
	overflow: auto;
	overflow-y: auto;
}

.collapsed > .open-folder,
.not-collapsed > .closed-folder {
	display: none;
}

.open-folder, .closed-folder {
	font-size: 20px;
}
</style>